'use client'

import {Badge, Grid, Icon, IconButton, useMediaQuery, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateGlobalLoading, updateSidebarStatus} from "@/redux/slices/settings";
import useStyles from "@/hooks/useStyles";
import CustomMenu from "@/components/layout/customMenu";
import {useCustomTheme} from "@/components/providers/themeContext/themeContextProvider";
import {useCustomLocale} from "@/components/providers/localesContext/localesProvider";
import "./layout.css"
import React from "react";
import {usersSelectors} from "@/redux/slices/users";
import {AsyncCookie} from "@/components/shared/client/utils";
import {useRouter} from "next/navigation";

const CustomHeader = ({sidePadding, headerHeight}: {
    sidePadding: { paddingLeft: number; paddingRight: number } | null;
    headerHeight: number
}) => {
    const {theme: selectedTheme, webContext, userContext} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const userContext = usersSelectors.getUserContext(state);
        const theme = settingsSelectors.getTheme(state);
        return {webContext, theme, userContext};
    }, shallowEqual);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {theme, updateTheme} = useCustomTheme();
    const muiTheme = useTheme();
    const {direction, changeDirection, changeLocale} = useCustomLocale();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
    const {dir} = useSelector((state) => {
        const dir = settingsSelectors.getDirection(state);
        return {dir};
    }, shallowEqual);
    const cookieName = process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "SESSION_ID";
    const router = useRouter();

    const pathname = window.location.pathname;

    const handleThemeChange = () => {
        updateTheme(theme === 1 ? 2 : 1)
    }

    const handleSidebarChange = () => {
        dispatch(updateSidebarStatus(null))
    }

    const handleDirectionChange = () => {
        changeDirection(direction === "ltr" ? "rtl" : "ltr")
    }

    const menuItems = [
        {
            label: t('global:logout'),
            icon: "logout",
            onClick: () => {
                dispatch(updateGlobalLoading(true));
                AsyncCookie.deleteCookie(cookieName)
                router.push(webContext?.loginUrl)
            }
        }
    ];
    if ([1].includes(userContext?.role)) {
        menuItems.unshift(
            {
                label: t('global:profile'),
                icon: "person2",
                onClick: () => {
                    if(pathname.indexOf('profile') === -1) {
                        dispatch(updateGlobalLoading(true));
                        router.push(`/profile`)
                    }
                }
            }
        )
    }

    const {df, appBarIcons, appBarIconSize, jcc, aic, appBarStyles} = useStyles();
    return <Grid
        className="we1 aic df pf tpl"
        sx={{
            zIndex: 100,
            background: theme => theme.palette?.backgrounds?.topBar,
            height: `${headerHeight}rem`,
            paddingLeft: sidePadding?.paddingLeft === 0 ? 0.5 : `${sidePadding?.paddingLeft}px`,
            paddingRight: sidePadding?.paddingRight === 0 ? 0.5 : `${sidePadding?.paddingRight}px`,
            borderBottom: theme => `1px solid ${theme.palette?.borders?.mainDivider}`,
        }}>
        <Grid className="df we1">
            <Grid container sx={{...df, flex: 1}}>
                <Grid sx={{...df, ...appBarIcons, ...jcc, ...aic, display: {xs: "flex", md: "none"}}}>
                    <IconButton onClick={handleSidebarChange} sx={{outline: 0}}><Icon
                        sx={{...appBarIconSize}}>menu_open</Icon></IconButton>
                </Grid>
                <Grid container className="df aic fwb" sx={{color: "texts.sidebar_main", ml: 1, mr: 1}}>
                    <Grid className="aic df" sx={{p: 0.5, width: "3rem"}}>
                        <img
                            src={selectedTheme === 1 ? `${webContext.logo_2_url}1.webp` : `${webContext.logo_1_url}1.webp`}
                            alt={t("companyLogo")}
                            style={{width: "100%"}}
                        />
                    </Grid>
                    <Grid
                        className="glow-text"
                        sx={{ml: 0.5, mr: 0.5, color: "texts.heading"}}>
                        {t(`global:${isMobile ? 'mobileAppName' : 'appName'}`)}
                    </Grid>
                </Grid>
            </Grid>
            {/*<Grid sx={{...df, ...appBarIcons, ...jcc, ...aic}}>*/}
            {/*    <IconButton onClick={handleDirectionChange} sx={{outline: 0}}>*/}
            {/*        <Icon*/}
            {/*            sx={{...appBarIconSize}}>{dir === "ltr" ? "format_textdirection_l_to_r" : "format_textdirection_r_to_l"}*/}
            {/*        </Icon>*/}
            {/*    </IconButton>*/}
            {/*</Grid>*/}
            <Grid sx={{...df, ...appBarIcons, ...jcc, ...aic}}>
                <IconButton onClick={handleThemeChange} sx={{outline: 0}}><Icon
                    sx={{...appBarIconSize}}>{theme === 1 ? "light_mode" : "dark_mode"}</Icon></IconButton>
            </Grid>
            <Grid sx={{...df, ...appBarIcons, ...jcc, ...aic}}>
                <IconButton sx={{outline: 0}}>
                    <Badge badgeContent={1} color="error" variant="dot" invisible={false}>
                        <Icon color="success" sx={{...appBarIconSize}}>notifications</Icon>
                    </Badge>
                </IconButton>
            </Grid>
            <Grid sx={{...df, ...appBarIcons, ...jcc, ...aic}}>
                <IconButton
                    sx={{outline: 0}}
                    onClick={() => {
                        if(pathname.indexOf('dashboard') === -1) {
                            dispatch(updateGlobalLoading(true));
                            router.push(webContext?.indexPage)
                        }
                    }}
                >
                    <Icon sx={{...appBarIconSize}}>settings</Icon>
                </IconButton>
            </Grid>
            <Grid sx={{...df, ...appBarIcons, ...jcc, ...aic}}>
                <CustomMenu mainIcon="person" items={menuItems}/>
            </Grid>
        </Grid>
    </Grid>
}

export default CustomHeader